<template>
  <b-card>
    <b-row class="my-3" v-if="!loading.form">
      <b-col cols="12" md="12">
        <h4 class="mb-2 text-center">
          <span class="text-primary">{{ customer_name }}</span>
        </h4>
      </b-col>
    </b-row>
    <template v-if="loading.form">
      <b-row class="justify-content-md-center mt-2">
        <b-spinner big variant="primary" />
      </b-row>
    </template>
    <template v-else>
      <div class="interview-score-panel mb-2">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <span>Score da Entrevista Individual</span>
            <strong>
              Total: {{ scoreTotalLabel }} | Classificacao: {{ classificationLabel }}
            </strong>
          </div>
          <div class="d-flex align-items-center">
            <b-form-group label="Feeling" class="mb-0 mr-2 feeling-input">
              <b-form-input
                v-model.number="feelingScore"
                type="number"
                min="0"
                max="5"
                step="1"
              />
            </b-form-group>
            <span :class="autosaveStatusClass">
              {{ autosaveStatusLabel }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-for="block in blocks"
        :key="block.id"
        class="interview-block mb-1"
      >
        <div
          class="interview-block-header"
          role="button"
          @click="toggleBlock(block.id)"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="isOpen(block.id) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="18"
              class="mr-1"
            />
            <span class="interview-block-title">{{ block.title }}</span>
          </div>
          <div class="d-flex align-items-center">
            <span :class="statusBadgeClass(block)" class="mr-1">
              {{ statusBadgeLabel(block) }}
            </span>
            <b-badge
              v-if="block.is_scorable"
              variant="light-primary"
            >
              {{ blockScoreLabel(block) }}
            </b-badge>
          </div>
        </div>

        <b-collapse :visible="isOpen(block.id)">
          <div class="interview-block-body">
            <p v-if="block.objective" class="interview-block-objective">
              {{ block.objective }}
            </p>

            <b-row
              v-for="question in block.questions"
              :key="question.id"
              class="mb-1"
            >
              <b-col cols="12" md="6" class="mt-1">
                <div>{{ question.question }}</div>
                <small v-if="question.objective" class="text-muted d-block mt-25">
                  <em>{{ question.objective }}</em>
                </small>
              </b-col>
              <b-col cols="12" md="6" class="mt-1">
                <b-form-group class="mb-0">
                  <v-select
                    v-if="isChoice(question)"
                    v-model="question.value"
                    :options="optionsFor(question)"
                    :reduce="(option) => option.value"
                    label="label"
                    placeholder="Selecione"
                  />
                  <b-form-textarea v-else v-model="question.value" />
                </b-form-group>
              </b-col>
            </b-row>

            <div v-if="block.is_scorable" class="interview-block-score mt-1">
              <b-row class="align-items-center">
                <b-col cols="12" md="6">
                  <strong>Score do bloco</strong>
                  <small class="text-muted d-block">
                    {{ statusHint(block) }}
                  </small>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-input
                    v-model.number="block.score"
                    type="number"
                    min="0"
                    max="5"
                    step="1"
                    placeholder="0 a 5"
                  />
                </b-col>
              </b-row>
            </div>

            <div v-if="block.tip" class="interview-block-tip mt-1">
              <div class="interview-block-tip-label">Dica</div>
              <p
                v-for="(paragraph, index) in tipParagraphs(block.tip)"
                :key="index"
                class="mb-0"
                :class="{ 'mt-50': index > 0 }"
              >
                {{ paragraph }}
              </p>
            </div>
          </div>
        </b-collapse>
      </div>

      <div class="interview-block mb-1">
        <div class="interview-block-header" role="button" @click="toggleBlock('other')">
          <div class="d-flex align-items-center">
            <feather-icon
              :icon="isOpen('other') ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="18"
              class="mr-1"
            />
            <span class="interview-block-title">Perguntas adicionais</span>
          </div>
        </div>
        <b-collapse :visible="isOpen('other')">
          <div class="interview-block-body">
            <b-row v-for="data in otherQuestions" :key="data.key" class="mb-1">
              <b-col cols="12" md="6" class="mt-1">
                <b-form-textarea v-model="data.question" placeholder="Pergunta" />
              </b-col>
              <b-col cols="12" md="6" class="mt-1">
                <b-form-group class="mb-0">
                  <b-form-textarea v-model="data.answer" placeholder="Resposta" />
                </b-form-group>
              </b-col>
            </b-row>
            <div class="d-flex mt-1 justify-content-start">
              <b-button
                :disabled="saving"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-success"
                @click="addQuestion"
              >
                Adicionar pergunta
              </b-button>
            </div>
          </div>
        </b-collapse>
      </div>

      <div class="d-flex mt-1 justify-content-end">
        <b-button
          :disabled="saving"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-primary"
          @click="goToLead"
          class="lead-buttons"
        >
          Voltar
        </b-button>
      </div>
    </template>
  </b-card>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BFormInput,
  BFormTextarea,
  BCol,
  BRow,
  BSpinner,
  BCollapse,
  BBadge,
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import * as types from "../store/types";
import _ from "lodash";

export default {
  components: {
    BButton,
    BCard,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BCol,
    BRow,
    BSpinner,
    BCollapse,
    BBadge,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      customer_name: "",
      saving: false,
      loading: {
        form: false,
      },
      blocks: [],
      otherQuestions: [],
      feelingScore: null,
      classification: null,
      classificationTag: null,
      savingStatus: "idle",
      lastAutosavedAt: null,
      hydrationComplete: false,
      debouncedAutosave: null,
      openBlocks: {},
    };
  },
  setup() {
    return { toast: useToast() };
  },
  computed: {
    autosaveSnapshot() {
      return {
        answers: this.buildAnswersPayload(),
        block_scores: this.buildBlockScores(),
        feeling_score: this.normalizedScore(this.feelingScore),
      };
    },
    localScoreTotal() {
      const blockTotal = this.blocks
        .filter((block) => block.is_scorable)
        .reduce((total, block) => total + Number(this.normalizedScore(block.score) || 0), 0);
      return blockTotal + Number(this.normalizedScore(this.feelingScore) || 0);
    },
    scoreTotalLabel() {
      return Number(this.localScoreTotal || 0).toFixed(1);
    },
    classificationLabel() {
      return this.classification || "Pendente";
    },
    autosaveStatusLabel() {
      const labels = {
        idle: "Aguardando alteracoes",
        saving: "Salvando...",
        saved: "Salvo",
        error: "Erro ao salvar",
      };
      return labels[this.savingStatus] || labels.idle;
    },
    autosaveStatusClass() {
      const classes = {
        idle: "badge badge-light-secondary",
        saving: "badge badge-light-warning",
        saved: "badge badge-light-success",
        error: "badge badge-light-danger",
      };
      return classes[this.savingStatus] || classes.idle;
    },
  },
  watch: {
    autosaveSnapshot: {
      deep: true,
      handler() {
        if (!this.hydrationComplete || this.loading.form || this.saving) {
          return;
        }
        this.scheduleAutosave();
      },
    },
  },
  created() {
    this.debouncedAutosave = _.debounce(this.autosave, 700);
  },
  mounted() {
    this.loading.form = true;
    this.getReport(this.$route.params.id)
      .then((response) => {
        this.customer_name = response.data.customer_name;
        this.applyBlocks(response.data.blocks || []);
        this.otherQuestions = this.parseOtherQuestions(
          response.data.report ? response.data.report.other_questions : null
        );
        this.feelingScore = this.extractScore(
          response.data.report ? response.data.report.feeling_score : null
        );
        this.classification =
          (response.data.report && response.data.report.classification) || null;
        this.classificationTag =
          (response.data.report && response.data.report.classification_tag) || null;
        this.restoreAutosaveBackup();
        this.initOpenBlocks();
        this.hydrationComplete = true;
        this.loading.form = false;
      })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar o candidato. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        setTimeout(() => {
          this.$router.back();
          this.loading.form = false;
        }, 2000);
      });
  },
  beforeDestroy() {
    if (this.debouncedAutosave) {
      this.debouncedAutosave.cancel();
    }
  },
  methods: {
    ...mapActions({
      getReport: types.GET_INDIVIDUAL_INTERVIEW_REPORT,
      saveIndividualInterviewReport: types.SAVE_INDIVIDUAL_INTERVIEW_REPORT,
      autosaveIndividualInterviewReport: types.AUTOSAVE_INDIVIDUAL_INTERVIEW_REPORT,
    }),
    applyBlocks(blocks) {
      this.blocks = blocks.map((block) => ({
        ...block,
        score: this.extractScore(block.score),
        questions: (block.questions || []).map((question) => ({
          ...question,
          value: question.value == null ? null : question.value,
        })),
      }));
    },
    initOpenBlocks() {
      const open = {};
      this.blocks.forEach((block) => {
        open[block.id] = false;
      });
      open.other = false;
      this.openBlocks = open;
    },
    toggleBlock(id) {
      this.$set(this.openBlocks, id, !this.openBlocks[id]);
    },
    isOpen(id) {
      return !!this.openBlocks[id];
    },
    isChoice(question) {
      return question.type === "select" || question.type === "radio";
    },
    optionsFor(question) {
      const options =
        question.config && Array.isArray(question.config.options)
          ? question.config.options
          : [];
      return options.map((option) => ({
        label: option.label != null ? option.label : option.value,
        value: option.value != null ? option.value : option.label,
      }));
    },
    tipParagraphs(tip) {
      return String(tip)
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0);
    },
    answeredCount(block) {
      return block.questions.filter((question) => this.isFilled(question.value)).length;
    },
    liveFieldsStatus(block) {
      const total = block.questions.length;
      const answered = this.answeredCount(block);
      if (total === 0) return "complete";
      if (answered === 0) return "empty";
      if (answered < total) return "missing";
      return "complete";
    },
    statusBadgeLabel(block) {
      const fields = this.liveFieldsStatus(block);
      if (block.is_scorable) {
        if (this.normalizedScore(block.score) !== null) return "Concluido";
        if (fields === "missing") return "Campos ausentes";
        if (fields === "empty") return "Pendente";
        return "Pontuacao pendente";
      }
      if (fields === "complete") return "Preenchido";
      if (fields === "missing") return "Campos ausentes";
      return "Pendente";
    },
    statusBadgeClass(block) {
      const fields = this.liveFieldsStatus(block);
      let variant = "secondary";
      if (block.is_scorable) {
        if (this.normalizedScore(block.score) !== null) variant = "success";
        else if (fields === "missing") variant = "warning";
        else if (fields === "complete") variant = "info";
        else variant = "secondary";
      } else if (fields === "complete") {
        variant = "success";
      } else if (fields === "missing") {
        variant = "warning";
      }
      return `badge badge-light-${variant}`;
    },
    statusHint(block) {
      const fields = this.liveFieldsStatus(block);
      if (fields === "missing") {
        return `Campos ausentes: ${this.answeredCount(block)}/${block.questions.length} respondidos.`;
      }
      if (fields === "empty") {
        return "Nenhuma pergunta respondida ainda.";
      }
      if (this.normalizedScore(block.score) === null) {
        return "Perguntas respondidas. Registre a pontuacao do bloco.";
      }
      return "Bloco pontuado.";
    },
    blockScoreLabel(block) {
      const score = this.normalizedScore(block.score);
      return score === null ? "-/5" : `${score}/5`;
    },
    addQuestion() {
      this.otherQuestions.push({
        question: undefined,
        answer: undefined,
        key: this.otherQuestions.length + 1,
      });
    },
    buildAnswersPayload() {
      const answers = {};
      this.blocks.forEach((block) => {
        block.questions.forEach((question) => {
          answers[question.id] =
            question.value == null ? "" : String(question.value);
        });
      });
      return answers;
    },
    buildBlockScores() {
      const scores = {};
      this.blocks
        .filter((block) => block.is_scorable)
        .forEach((block) => {
          scores[block.code] = this.normalizedScore(block.score);
        });
      return scores;
    },
    onSubmit() {
      this.saving = true;

      const questions = _.filter(this.otherQuestions, (item) => item.question || item.answer);

      this.saveIndividualInterviewReport({
        customer_id: this.$route.params.id,
        other_questions: JSON.stringify(questions),
        answers: this.buildAnswersPayload(),
        block_scores: this.buildBlockScores(),
        feeling_score: this.normalizedScore(this.feelingScore),
      })
        .then(() => {
          localStorage.removeItem(this.autosaveStorageKey());
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "Questionário da entrevista salvo com sucesso.",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.goToLead();
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao salvar o relatório da entrevista. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    goToLead() {
      this.$router.push({
        name: "lead-pn-profile",
        params: { id: this.$route.params.id },
      });
    },
    isFilled(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },
    normalizedScore(value) {
      if (value && typeof value === "object" && value.score !== undefined) {
        value = value.score;
      }
      if (value === undefined || value === null || value === "") {
        return null;
      }
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return null;
      }
      return Math.max(0, Math.min(5, numericValue));
    },
    extractScore(value) {
      return this.normalizedScore(value);
    },
    parseOtherQuestions(value) {
      if (!value) {
        return [];
      }
      if (Array.isArray(value)) {
        return value;
      }
      try {
        return JSON.parse(value);
      } catch (error) {
        return [];
      }
    },
    scheduleAutosave() {
      this.savingStatus = "saving";
      if (this.debouncedAutosave) {
        this.debouncedAutosave();
      }
    },
    async autosave() {
      if (!this.hydrationComplete || this.loading.form || this.saving) {
        return;
      }

      const payload = this.autosaveSnapshot;

      try {
        const response = await this.autosaveIndividualInterviewReport({
          customerId: this.$route.params.id,
          payload,
        });
        const data = response && response.data ? response.data : {};
        this.classification = data.classification || this.classification;
        this.classificationTag = data.classification_tag || this.classificationTag;
        this.savingStatus = "saved";
        this.lastAutosavedAt = new Date().toISOString();
        localStorage.removeItem(this.autosaveStorageKey());
      } catch (error) {
        this.savingStatus = "error";
        localStorage.setItem(
          this.autosaveStorageKey(),
          JSON.stringify({
            saved_at: new Date().toISOString(),
            payload,
          })
        );
      }
    },
    restoreAutosaveBackup() {
      const raw = localStorage.getItem(this.autosaveStorageKey());
      if (!raw) {
        return;
      }

      try {
        const backup = JSON.parse(raw);
        if (!backup || !backup.payload) {
          return;
        }

        const answers = backup.payload.answers || {};
        const scores = backup.payload.block_scores || {};

        this.blocks.forEach((block) => {
          block.questions.forEach((question) => {
            if (answers[question.id] !== undefined && answers[question.id] !== "") {
              question.value = answers[question.id];
            }
          });
          if (block.is_scorable && scores[block.code] !== undefined) {
            block.score = this.extractScore(scores[block.code]);
          }
        });

        if (backup.payload.feeling_score !== undefined) {
          this.feelingScore = this.extractScore(backup.payload.feeling_score);
        }
        this.savingStatus = "error";
      } catch (error) {
        localStorage.removeItem(this.autosaveStorageKey());
      }
    },
    autosaveStorageKey() {
      return `recruitment-individual-interview-${this.$route.params.id}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.interview-score-panel {
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 1rem;
}

.dark-layout .interview-score-panel {
  border-color: #1e263a;
}

.feeling-input {
  width: 110px;
}

.interview-block {
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  overflow: hidden;
}

.dark-layout .interview-block {
  border-color: #1e263a;
}

.interview-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f8f8;
  cursor: pointer;
  user-select: none;
}

.dark-layout .interview-block-header {
  background-color: #161d31;
}

.interview-block-title {
  font-weight: 600;
  font-size: 15px;
}

.interview-block-body {
  padding: 1rem;
}

.interview-block-objective {
  white-space: pre-line;
  color: #6e6b7b;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.dark-layout .interview-block-objective {
  color: #b4b7bd;
}

.interview-block-score {
  border-top: 1px dashed #dcdcdc;
  padding-top: 1rem;
}

.dark-layout .interview-block-score {
  border-color: #1e263a;
}

.interview-block-tip {
  background-color: #fff8e6;
  border-left: 4px solid #ff9f43;
  border-radius: 6px;
  padding: 0.75rem 1rem;
}

.dark-layout .interview-block-tip {
  background-color: #283046;
  border-left-color: #ff9f43;
}

.interview-block-tip-label {
  font-weight: 600;
  color: #ff9f43;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.04em;
}

textarea {
  min-height: 80px !important;
}
</style>
